import { BtnPrimary, AmbientParticles } from '../ui/components';
import { currentPlayers } from '../screens/GameSetup';
import { t } from '../i18n';
import { sanitizeHtml } from '../utils/sanitize';
import { state } from '../store';
import { reportRoundResult } from './TournamentEngine';
import { shuffle } from '../utils/random';
import { cardsManager } from './cardsManager';

let currentWords: string[] = [];
let currentIndex: number = 0;
let currentGameTitle: string = '';
let currentPlayerIndex: number = 0;
let gameState: 'setup' | 'countdown' | 'play' = 'setup';
let countdownTimer: number = 3;
let timerInterval: number | null = null;
let currentGameId: string = 'wai';
export let _wordRevealed: boolean = false;
let revealTimeout: number | null = null;
let _useCardsManager = false;

// Tournament countdown state
let trnCountdownInterval: number | null = null;
let trnSecondsLeft: number = 0;

declare global {
  interface Window {
    trnTimerJudgement: (success: boolean) => void;
    startTimerGame: (title: string, words: string[], gameId?: string) => void;
    timerGameReady: () => void;
    timerGameNext: () => void;
    timerGameFinish: () => void;
    revealTimerWord: () => void;
  }
}

function isTournamentPlay(): boolean {
  return !!state.tournament && state.tournament.status === 'playing';
}

function startTrnCountdown(seconds: number): void {
  stopTrnCountdown();
  trnSecondsLeft = seconds;
  const update = () => {
    const el = document.getElementById('trn-timer-num');
    if (el) el.textContent = String(trnSecondsLeft);
    if (trnSecondsLeft <= 0) {
      stopTrnCountdown();
      window.trnTimerJudgement(false);
    } else {
      trnSecondsLeft--;
    }
  };
  update();
  trnCountdownInterval = window.setInterval(update, 1000);
}

function stopTrnCountdown(): void {
  if (trnCountdownInterval) { clearInterval(trnCountdownInterval); trnCountdownInterval = null; }
}

window.trnTimerJudgement = (success: boolean) => {
  stopTrnCountdown();
  reportRoundResult(success);
  const tr = state.tournament;
  if (tr && tr.status === 'finished') {
    window.navTo('tournament-victory');
  } else {
    window.navTo('tournament-board');
  }
};

window.startTimerGame = async (title: string, words: string[], gameId: string = 'wai') => {
  currentGameTitle = title;
  currentIndex = 0;
  currentPlayerIndex = 0;
  currentGameId = gameId;
  _wordRevealed = false;
  gameState = 'setup';

  if (words && words.length > 0) {
    // Tournament mode — use provided words directly
    _useCardsManager = false;
    currentWords = shuffle([...words]);
  } else {
    // cardsManager mode
    _useCardsManager = true;
    const card = await cardsManager.getNext();
    if (!card) {
      (window as any).showToast?.(t('cards_finished'), 'info');
      window.navTo('games');
      return;
    }
    currentWords = [card.text];
  }

  window.navTo('timer-game');
};

window.revealTimerWord = () => {
  _wordRevealed = true;
  const wordContainer = document.getElementById('timer-word-reveal');
  if (wordContainer) {
    wordContainer.style.filter = 'blur(0px)';
    wordContainer.style.userSelect = 'text';
  }
  if (revealTimeout) clearTimeout(revealTimeout);
  revealTimeout = window.setTimeout(() => {
    _wordRevealed = false;
    const el = document.getElementById('timer-word-reveal');
    if (el) {
      el.style.filter = 'blur(12px)';
      el.style.userSelect = 'none';
    }
  }, 3000);
};

window.timerGameReady = () => {
  gameState = 'countdown';
  countdownTimer = 3;
  window.renderApp();

  if (timerInterval) clearInterval(timerInterval);
  timerInterval = window.setInterval(() => {
    countdownTimer--;
    if (countdownTimer <= 0) {
      if (timerInterval) clearInterval(timerInterval);
      gameState = 'play';
      window.renderApp();
    } else {
      const countEl = document.getElementById('timer-countdown-number');
      if (countEl) {
        countEl.textContent = String(countdownTimer);
      } else {
        window.renderApp();
      }
    }
  }, 1000);
};

window.timerGameNext = async () => {
  _wordRevealed = false;
  if (revealTimeout) { clearTimeout(revealTimeout); revealTimeout = null; }

  if (_useCardsManager) {
    const next = await cardsManager.getNext();
    if (next) {
      currentWords = [next.text];
      currentIndex = 0;
    } else {
      (window as any).showToast?.(t('cards_finished'), 'info');
      window.navTo('games');
      return;
    }
  } else {
    currentIndex = (currentIndex + 1) % currentWords.length;
  }

  currentPlayerIndex = (currentPlayerIndex + 1) % currentPlayers.length;
  gameState = 'setup';
  window.renderApp();
};

window.timerGameFinish = () => {
     if (timerInterval) clearInterval(timerInterval);
     window.navTo('games');
};

export function cleanupTimerGame(): void {
  stopTrnCountdown();
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  if (revealTimeout) { clearTimeout(revealTimeout); revealTimeout = null; }
  _wordRevealed = false;
  gameState = 'setup';
  if (_useCardsManager) cardsManager.cleanup();
  _useCardsManager = false;
}

function getWordFontSize(word: string): string {
  const len = word.length;
  if (len <= 8) return 'text-[48px]';
  if (len <= 12) return 'text-[40px]';
  if (len <= 16) return 'text-[32px]';
  if (len <= 22) return 'text-[26px]';
  return 'text-[22px]';
}

export function renderTimerGameEngine(): string {
  if (!currentPlayers || currentPlayers.length === 0) {
    if (!isTournamentPlay()) return '';
  }
  const isTrn = isTournamentPlay();

  const trnTeam = isTrn
    ? state.tournament!.teams[state.tournament!.currentTeamIndex]
    : null;
  const trnPlayer = isTrn
    ? (() => {
        const team = trnTeam!;
        const cursor = state.tournament!.currentPlayerInTeam[team.id] ?? 0;
        const pid = team.playerIds[cursor % team.playerIds.length];
        const p = currentPlayers.find(pp => pp.id === pid);
        return p ? { emoji: p.emoji, name: p.name } : null;
      })()
    : null;

  const headerEntity = isTrn
    ? (trnPlayer || { emoji: trnTeam!.emoji, name: trnTeam!.name })
    : currentPlayers[currentPlayerIndex % currentPlayers.length];
  const turnLabel = isTrn ? t('trn_play_player_label') : t('game_turn_label');

  const currentWord = currentWords[currentIndex] || '';
  const wordStyle = currentWord.length > 25 ? 'overflow-wrap: break-word; word-break: break-word;' : 'white-space: nowrap;';

  let contentHtml = '';
  let bottomHtml = '';

  if (isTrn) {
    const trn = state.tournament!;
    const dur = trn.settings.turnDurationSec;
    const teamLabel = `${trnTeam!.emoji} ${sanitizeHtml(trnTeam!.name)}`;

    if (gameState === 'setup') {
      stopTrnCountdown();

      let intro = '';
      if (currentGameId === 'wai') {
        intro = `
          <div class="text-[80px] mb-4 animate-[iconFloat_3s_ease-in-out_infinite]">${headerEntity.emoji}</div>
          <div class="font-display text-[28px] font-extrabold text-white leading-[1.2]">${sanitizeHtml(headerEntity.name)}</div>
          <div class="text-[15px] font-bold text-t-secondary mt-[6px]">${t('trn_play_forehead')}</div>
        `;
      } else {
        const actionText = currentGameId === 'describe' ? t('trn_play_describing') : t('trn_play_acting');
        const actionIcon = currentGameId === 'describe' ? '💬' : '🙌';
        intro = `
          <div class="text-[60px] mb-2 animate-[iconFloat_3s_ease-in-out_infinite]">${headerEntity.emoji}</div>
          <div class="font-display text-[22px] font-extrabold text-white">${sanitizeHtml(headerEntity.name)}</div>
          <div class="text-[14px] text-t-secondary font-semibold mt-[2px]">${actionText}</div>

          <div class="w-full mt-[16px] p-[24px] bg-glass border border-b-glass rounded-xl text-center relative overflow-hidden">
            <div class="text-[11px] font-extrabold tracking-[1.5px] text-t-muted mb-[10px] uppercase">${actionIcon} ${t('timer_your_word_label')}</div>
            <div
              id="timer-word-reveal"
              data-action="reveal-timer-word"
              style="filter: blur(12px); user-select: none; cursor: pointer; transition: filter 0.3s ease;"
              class="font-display text-[32px] font-extrabold text-white leading-[1.2] select-none"
            >${sanitizeHtml(currentWord)}</div>
            <div class="text-[12px] text-t-muted mt-[10px] font-semibold">${t('timer_your_word')}</div>
          </div>
        `;
      }

      contentHtml = `
        <div class="flex-1 flex flex-col items-center justify-center text-center p-[16px_24px] z-10 relative">
          ${intro}
          <div class="mt-[16px] text-[12px] font-extrabold tracking-[1.5px] text-t-muted">${t('trn_play_get_ready')}</div>
        </div>
      `;
      bottomHtml = `
        <div class="p-[20px_16px_32px] w-full mt-auto relative z-10">
          ${BtnPrimary({ text: t('trn_play_ready_btn'), onClick: 'timer-game-ready' })}
        </div>
      `;
    } else if (gameState === 'countdown') {
      stopTrnCountdown();
      contentHtml = `
        <div class="flex-1 flex flex-col items-center justify-center text-center p-[60px_20px] w-full z-10 relative">
          <div id="timer-countdown-number" class="font-display text-[120px] font-extrabold bg-grad-primary bg-clip-text text-transparent animate-[timerWarning_0.5s_infinite_alternate] leading-none mb-4">${countdownTimer}</div>
        </div>
      `;
      bottomHtml = '';
    } else {
      if (dur > 0 && trnCountdownInterval === null) {
        setTimeout(() => startTrnCountdown(dur), 50);
      }

      contentHtml = `
        <div class="flex-1 flex flex-col items-center justify-center w-full z-10 relative px-[16px]">
          <div class="w-full bg-[linear-gradient(135deg,rgba(45,107,255,0.1),rgba(0,229,255,0.06))] border border-neon-blue/25 rounded-xl text-center p-[60px_20px] shadow-[0_8px_32px_rgba(0,0,0,0.5)] animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)]">
            <div class="font-display ${getWordFontSize(currentWord)} font-extrabold text-white leading-[1.1] drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)] text-center" style="${wordStyle}">${sanitizeHtml(currentWord)}</div>
          </div>
        </div>
      `;

      const timerLabelHtml = (dur > 0) ? `
        <div class="text-center mb-[8px]">
          <span class="text-[11px] font-extrabold tracking-[1.5px] text-t-muted">${t('trn_play_timer_label')}: </span>
          <span id="trn-timer-num" class="font-display text-[28px] font-extrabold text-neon-pink">${dur}</span>
          <span class="text-[12px] text-t-muted">s</span>
        </div>
      ` : '';

      bottomHtml = `
        <div class="p-[20px_16px_32px] w-full mt-auto relative z-10">
          ${timerLabelHtml}
          <div class="flex gap-[10px]">
            <button class="flex-1 py-[15px] px-[20px] bg-glass border border-neon-red/40 rounded-pill text-white font-body text-[15px] font-extrabold active:scale-[0.97] transition" data-action="trn-timer-judgement" data-result="false">
              ${t('trn_play_failed')}
            </button>
            <button class="flex-1 py-[15px] px-[20px] bg-grad-green rounded-pill text-white font-body text-[15px] font-extrabold shadow-glow-green active:scale-[0.97] transition" data-action="trn-timer-judgement" data-result="true">
              ${t('trn_play_guessed')}
            </button>
          </div>
        </div>
      `;
    }

    return `
      <div class="absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden bg-void z-20" id="screen-timer-game">
        <div class="fixed top-[-20%] left-[-10%] w-[70vw] h-[60vh] bg-[radial-gradient(ellipse,rgba(160,32,240,0.15)_0%,transparent_70%)] pointer-events-none z-0"></div>
        <div class="fixed bottom-[-10%] right-[-15%] w-[60vw] h-[50vh] bg-[radial-gradient(ellipse,rgba(255,45,107,0.12)_0%,transparent_70%)] pointer-events-none z-0"></div>
        ${AmbientParticles()}

        <div class="flex flex-col items-center w-full shrink-0 relative z-10 pt-[16px] pb-[8px]">
          <div class="flex flex-col items-center w-full px-[20px] relative">
             <div class="absolute left-[20px] top-[0px] w-[38px] h-[38px] bg-[rgba(255,255,255,0.05)] border border-white/10 rounded-full flex items-center justify-center cursor-pointer text-[18px] transition-all duration-150 active:scale-[0.92] active:bg-[rgba(255,255,255,0.1)] pointer-events-auto z-20" data-action="trn-exit-confirm">←</div>
             <div class="font-display text-[20px] font-extrabold text-white leading-[38px] text-center w-full px-[40px] z-10">🏆 ${teamLabel}</div>
          </div>
          ${trnPlayer ? `
            <div class="mt-[12px] bg-[rgba(255,255,255,0.06)] backdrop-blur-md border border-white/10 rounded-pill px-[16px] py-[6px] text-[14px] font-extrabold text-white flex items-center justify-center gap-[6px] shadow-[0_0_12px_rgba(255,255,255,0.05)] text-center w-max mx-auto relative z-10">
              <span class="text-[12px] tracking-[0.5px] uppercase text-t-secondary mr-[2px]">${turnLabel}</span> <span class="text-[18px] leading-none">${trnPlayer.emoji}</span> <span>${sanitizeHtml(trnPlayer.name)}</span>
            </div>
          ` : ''}
        </div>

        ${contentHtml}
        ${bottomHtml}
      </div>
    `;
  }

  // ─── Соло-режим (не-турнир) ───
  const currentPlayer = currentPlayers[currentPlayerIndex % currentPlayers.length];

  if (gameState === 'setup') {
    if (currentGameId === 'wai') {
      contentHtml = `
        <div class="flex-1 flex flex-col items-center justify-center text-center p-[32px_24px] z-10 relative">
          <div class="text-[80px] mb-4 animate-[iconFloat_3s_ease-in-out_infinite]">${currentPlayer.emoji}</div>
          <div class="font-display text-[32px] font-extrabold text-white leading-[1.2] mb-2">${sanitizeHtml(currentPlayer.name)}</div>
          <div class="text-[18px] font-bold text-t-secondary">${t('timer_forehead')}</div>
        </div>
      `;
      bottomHtml = `
        <div class="p-[20px_16px_32px] w-full mt-auto relative z-10">
          ${BtnPrimary({ text: t('timer_ready_btn'), onClick: 'timer-game-ready' })}
        </div>
      `;
    } else {
      const actionText = currentGameId === 'describe' ? t('timer_describe_action') : t('timer_act_action');
      const actionIcon = currentGameId === 'describe' ? '💬' : '🙌';
      contentHtml = `
        <div class="flex-1 flex flex-col items-center justify-center px-[16px] z-10 relative gap-[20px]">
          <div class="text-center">
            <div class="text-[80px] animate-[iconFloat_3s_ease-in-out_infinite]">${currentPlayer.emoji}</div>
            <div class="font-display text-[28px] font-extrabold text-white mt-[8px]">${sanitizeHtml(currentPlayer.name)}</div>
            <div class="text-[15px] text-t-secondary font-semibold mt-[4px]">${actionText}</div>
          </div>
          <div class="w-full p-[24px] bg-glass border border-b-glass rounded-xl text-center relative overflow-hidden">
            <div class="text-[11px] font-extrabold tracking-[1.5px] text-t-muted mb-[10px] uppercase">${actionIcon} ${t('timer_your_word_label')}</div>
            <div
              id="timer-word-reveal"
              data-action="reveal-timer-word"
              style="filter: blur(12px); user-select: none; cursor: pointer; transition: filter 0.3s ease;"
              class="font-display text-[36px] font-extrabold text-white leading-[1.2] select-none"
            >${sanitizeHtml(currentWords[currentIndex] || '')}</div>
            <div class="text-[12px] text-t-muted mt-[10px] font-semibold">${t('timer_your_word')}</div>
          </div>
        </div>
      `;
      bottomHtml = `
        <div class="p-[20px_16px_32px] w-full mt-auto relative z-10">
          ${BtnPrimary({ text: t('timer_next'), onClick: 'timer-game-next' })}
        </div>
      `;
    }
  } else if (gameState === 'countdown') {
    contentHtml = `
      <div class="flex-1 flex flex-col items-center justify-center text-center p-[60px_20px] w-full z-10 relative">
        <div id="timer-countdown-number" class="font-display text-[120px] font-extrabold bg-grad-primary bg-clip-text text-transparent animate-[timerWarning_0.5s_infinite_alternate] leading-none mb-4">${countdownTimer}</div>
      </div>
    `;
    bottomHtml = '';
  } else if (gameState === 'play') {
    contentHtml = `
      <div class="flex-1 flex flex-col items-center justify-center w-full z-10 relative px-[16px]">
        <div class="w-full bg-[linear-gradient(135deg,rgba(45,107,255,0.1),rgba(0,229,255,0.06))] border border-neon-blue/25 rounded-xl text-center p-[60px_20px] shadow-[0_8px_32px_rgba(0,0,0,0.5)] animate-[taskSlide_0.35s_cubic-bezier(.4,0,.2,1)]">
          <div class="font-display ${getWordFontSize(currentWord)} font-extrabold text-white leading-[1.1] drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)] text-center" style="${wordStyle}">${sanitizeHtml(currentWord)}</div>
        </div>
      </div>
    `;
    bottomHtml = `
      <div class="p-[20px_16px_32px] w-full mt-auto relative z-10">
        ${BtnPrimary({ text: t('timer_next').replace(' →', ''), onClick: 'timer-game-next' })}
      </div>
    `;
  }

  return `
    <div class="absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden bg-void z-20" id="screen-timer-game">
      <div class="fixed top-[-20%] left-[-10%] w-[70vw] h-[60vh] bg-[radial-gradient(ellipse,rgba(160,32,240,0.15)_0%,transparent_70%)] pointer-events-none z-0"></div>
      <div class="fixed bottom-[-10%] right-[-15%] w-[60vw] h-[50vh] bg-[radial-gradient(ellipse,rgba(255,45,107,0.12)_0%,transparent_70%)] pointer-events-none z-0"></div>
      ${AmbientParticles()}

      <div class="flex flex-col items-center w-full shrink-0 relative z-10 pt-[16px] pb-[8px]">
        <div class="flex flex-col items-center w-full px-[20px] relative">
           <div class="absolute left-[20px] top-[0px] w-[38px] h-[38px] bg-[rgba(255,255,255,0.05)] border border-white/10 rounded-full flex items-center justify-center cursor-pointer text-[18px] transition-all duration-150 active:scale-[0.92] active:bg-[rgba(255,255,255,0.1)] pointer-events-auto z-20" data-action="timer-game-finish">←</div>
           <div class="font-display text-[20px] font-extrabold text-white leading-[38px] text-center w-full px-[40px] z-10">${sanitizeHtml(currentGameTitle)}</div>
        </div>
        <div class="mt-[12px] bg-[rgba(255,255,255,0.06)] backdrop-blur-md border border-white/10 rounded-pill px-[16px] py-[6px] text-[14px] font-extrabold text-white flex items-center justify-center gap-[6px] shadow-[0_0_12px_rgba(255,255,255,0.05)] text-center w-max mx-auto relative z-10">
          <span class="text-[12px] tracking-[0.5px] uppercase text-t-secondary mr-[2px]">${t('game_turn_label')}</span> <span class="text-[18px] leading-none">${currentPlayer.emoji}</span> <span>${sanitizeHtml(currentPlayer.name)}</span>
        </div>
      </div>

      ${contentHtml}
      ${bottomHtml}
    </div>
  `;
}
